import React from 'react';
import { ajaxGet, ajaxPost, AjaxResponseType, showErrorFlush } from '../../common/form/FrekulForm';
import DeferredUtility from '../../common/utility/DeferredUtility';
import { MusicComposerEntity, MusicEntity, MusicFeaturingArtistEntity, MusicLyricWriterEntity, MusicPerformanceRoleEntity, MusicProductionAndEngineeringRoleEntity } from '../types/MusicEntityTypes';
import { MusicAlbumEntity, MusicAlbumTrackEntity } from '../types/MusicAlbumEntityTypes';
import { TunecoreReleaseTrackEntity } from '../types/TunecoreReleaseEntityTypes';

export const defaultMusicDistributionClient = {
  signUp(mbmail: string, password: string, agreement: boolean): Promise<AjaxResponseType> {
    const postData = {
      artist_registration_type: 'signup',
      artist_registration_mbmail: mbmail,
      artist_registration_password: password,
      artist_registration_agreement: agreement ? '1' : '0',
    };
    return ajaxPost('/music_albums/ajax_register', postData, {}, true);
  },
  signIn(mbmail: string, password: string): Promise<AjaxResponseType> {
    const postData = {
      artist_registration_type: 'auth',
      artist_registration_mbmail: mbmail,
      artist_registration_password: password,
      artist_registration_agreement: '1',
    };
    return ajaxPost('/music_albums/ajax_register', postData, {}, true);
  },
  createMusicAlbum(options: {
    is_multiple: string,
    title: string,
    title_kana: string,
    title_en: string,
    genre: string,
    image: FileList|null,
    image_delete: string,
    music_album_artists: { name: string, name_kana: string, name_en: string }[],
  }): Promise<AjaxResponseType & {
    music_album_id: number,
  }> {
    const postData: { [key: string]: string|File } = {
      is_multiple: options.is_multiple,
      title: options.title,
      title_kana: options.title_kana,
      title_en: options.title_en,
      genre: options.genre,
      image_delete: options.image_delete,
    };
    if (options.image !== null) {
      postData.image = options.image[0];
    }
    options.music_album_artists.forEach((musicAlbumArtist, index: number) => {
      if (index === 0 || musicAlbumArtist.name !== '' || musicAlbumArtist.name_kana !== '' || musicAlbumArtist.name_en !== '') {
        postData[`music_album_artists[${index}][name]`] = musicAlbumArtist.name;
        postData[`music_album_artists[${index}][name_kana]`] = musicAlbumArtist.name_kana;
        postData[`music_album_artists[${index}][name_en]`] = musicAlbumArtist.name_en;
      }
    });
    return ajaxPost('/music_albums/ajax_create', postData, {}, true);
  },
  updateMusicAlbum(musicAlbumId: number, options: {
    is_multiple: string,
    title: string,
    title_kana: string,
    title_en: string,
    genre: string,
    image: FileList|null,
    image_delete: string,
    music_album_artists: { name: string, name_kana: string, name_en: string }[],
  }): Promise<AjaxResponseType> {
    const postData: { [key: string]: string|File } = {
      is_multiple: options.is_multiple,
      title: options.title,
      title_kana: options.title_kana,
      title_en: options.title_en,
      genre: options.genre,
      image_delete: options.image_delete,
    };
    if (options.image !== null) {
      postData.image = options.image[0];
    }
    options.music_album_artists.forEach((musicAlbumArtist, index: number) => {
      if (index === 0 || musicAlbumArtist.name !== '' || musicAlbumArtist.name_kana !== '' || musicAlbumArtist.name_en !== '') {
        postData[`music_album_artists[${index}][name]`] = musicAlbumArtist.name;
        postData[`music_album_artists[${index}][name_kana]`] = musicAlbumArtist.name_kana;
        postData[`music_album_artists[${index}][name_en]`] = musicAlbumArtist.name_en;
      }
    });
    return ajaxPost(`/music_albums/ajax_update/${musicAlbumId}`, postData, {}, true);
  },
  publishMusicAlbum(musicAlbumId: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_publish/${musicAlbumId}`, {}, {}, true);
  },
  cancelMusicAlbum(musicAlbumId: number, cancelReason: string, cancelReasonOthers: string): Promise<AjaxResponseType> {
    const postData: { [key: string]: string|File } = {
      cancel_reason: cancelReason,
    };
    if (cancelReason === 'others') {
      postData['cancel_reason_others'] = cancelReasonOthers;
    }
    return ajaxPost(`/music_albums/ajax_cancel/${musicAlbumId}`, postData, {}, true);
  },
  removeMusicAlbum(musicAlbumId: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_remove/${musicAlbumId}`, {}, {}, true);
  },
  getMusicAlbums(): Promise<AjaxResponseType & {
    musicAlbums: (MusicAlbumEntity & {
      music_album_tracks: (MusicAlbumTrackEntity & {
        music: MusicEntity,
      })[],
    })[],
  }> {
    return ajaxGet('/music_albums/ajax_get_music_albums');
  },
  addMusicAlbumTracks(musicAlbumId: number, musicIds: number[]): Promise<AjaxResponseType> {
    const postData: { [key: string]: string|File } = {};
    musicIds.forEach((musicId, index) => {
      postData[`music_ids[${index}]`] = `${musicId}`;
    });
    return ajaxPost(`/music_albums/ajax_add_tracks/${musicAlbumId}`, postData, {}, true);
  },
  getMusicAlbumTracks(musicAlbumId: number): Promise<AjaxResponseType & {
    musicAlbumTracks: (MusicAlbumTrackEntity & {
      music: MusicEntity & {
        music_featuring_artists: MusicFeaturingArtistEntity[],
        music_composers: MusicComposerEntity[],
        music_lyric_writers: MusicLyricWriterEntity[],
        music_production_and_engineering_roles: MusicProductionAndEngineeringRoleEntity[],
        music_performance_roles: MusicPerformanceRoleEntity[],
        tunecore_release_track: TunecoreReleaseTrackEntity,
      },
    })[],
  }> {
    return ajaxGet(`/music_albums/ajax_get_tracks/${musicAlbumId}`);
  },
  sortMusicAlbumTracks(musicAlbumId: number, trackNos: number[]): Promise<AjaxResponseType> {
    const postData: { [key: string]: string|File } = {};
    trackNos.forEach((trackNo, index) => {
      postData[`track_nos[${index}]`] = `${trackNo}`;
    });
    return ajaxPost(`/music_albums/ajax_sort_tracks/${musicAlbumId}`, postData, {}, true);
  },
  movePrevMusicAlbumTrack(musicAlbumId: number, musicAlbumTrackId: number, trackNo: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_set_track_no/${musicAlbumId}/${musicAlbumTrackId}`, { track_no: `${trackNo - 1}` }, {}, true);
  },
  moveNextMusicAlbumTrack(musicAlbumId: number, musicAlbumTrackId: number, trackNo: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_set_track_no/${musicAlbumId}/${musicAlbumTrackId}`, { track_no: `${trackNo + 1}` }, {}, true);
  },
  deleteMusicAlbumTrack(musicAlbumId: number, musicAlbumTrackId: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_remove_track/${musicAlbumId}/${musicAlbumTrackId}`, {}, {}, true);
  },
  validateMusicAlbumTracks(musicAlbumId: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_validate_track_selected/${musicAlbumId}`, {}, {}, true);
  },
  validateEditedMusicAlbumTracks(musicAlbumId: number): Promise<AjaxResponseType> {
    return ajaxPost(`/music_albums/ajax_validate_track_edited/${musicAlbumId}`, {}, {}, true);
  },
  getMusics(page: number): Promise<AjaxResponseType & {
    musics: MusicEntity[],
  }> {
    return ajaxGet(`/music_albums/ajax_get_musics/${page}`);
  },
  updateMusic(musicId: number, options: {
    title: string,
    title_kana: string,
    title_en: string,
    version: string,
    version_kana: string,
    version_en: string,
    language: string,
    is_adult: string,
    is_inst: string,
    isrc: string,
    has_version: string,
    version_type: string,
    place: string,
    place_kana: string,
    place_en: string,
    year: string,
    year_kana: string,
    year_en: string,
    city: string,
    city_kana: string,
    city_en: string,
    remix_name: string,
    remix_name_kana: string,
    remix_name_en: string,
    remixer_name: string,
    remixer_name_en: string,
    music_featuring_artists: { name: string, name_kana: string, name_en: string }[],
    music_lyric_writers: { name: string, name_en: string }[],
    music_composers: { name: string, name_en: string }[],
    music_production_and_engineering_roles: { role: string, name: string, name_en: string }[],
    music_performance_roles: { role: string, name: string, name_en: string }[],
  }): Promise<AjaxResponseType> {
    const postData: { [key: string]: string|File } = {
      title: options.title,
      title_kana: options.title_kana,
      title_en: options.title_en,
      version: options.version,
      version_kana: options.version_kana,
      version_en: options.version_en,
      language: options.language,
      is_adult: options.is_adult,
      is_inst: options.is_inst,
      isrc: options.isrc,
    };
    if (options.has_version === '0' || options.version_type === '') {
      postData.version = '';
      postData.version_kana = '';
      postData.version_en = '';
    } else if (options.version_type === 'instrumental') {
      postData.version = 'Instrumental';
      postData.version_kana = 'いんすとぅるめんたる';
      postData.version_en = 'Instrumental';
    } else if (options.version_type === 'offvocal') {
      postData.version = 'Off Vocal';
      postData.version_kana = 'おふぼーかる';
      postData.version_en = 'Off Vocal';
    } else if (options.version_type === 'karaoke') {
      postData.version = 'Karaoke Ver.';
      postData.version_kana = 'からおけばーじょん';
      postData.version_en = 'Karaoke Ver.';
    } else if (options.version_type === 'acoustic') {
      postData.version = 'Acoustic Ver.';
      postData.version_kana = 'あこーすてぃっくばーじょん';
      postData.version_en = 'Acoustic Ver.';
    } else if (options.version_type === 'live') {
      // eslint-disable-next-line max-len
      if ([options.place, options.place_kana, options.place_en, options.city, options.city_kana, options.city_en, options.year, options.year_kana, options.year_en].filter((item) => item.trim().length === 0).length > 0) {
        const name = 'version';
        const msg = 'バージョン情報をすべて入力してください';
        showErrorFlush(msg);
        return DeferredUtility.resolvedPromise({ result: false, msg, errors: { [name]: [['runtime', msg]] } });
      }
      postData.version = `Live at ${options.place},${options.city},${options.year}`;
      postData.version_kana = `らいぶあっと${options.place_kana}${options.city_kana}${options.year_kana}`;
      postData.version_en = `Live at ${options.place_en},${options.city_en},${options.year_en}`;
    } else if (options.version_type === 'remix') {
      postData.version = options.remix_name;
      postData.version_kana = options.remix_name_kana;
      postData.version_en = options.remix_name_en;
      postData['remixer_name'] = options.remixer_name;
      postData['remixer_name_en'] = options.remixer_name_en;
    } else if (options.version_type === 'other') {
      postData.version = options.version;
      postData.version_kana = options.version_kana;
      postData.version_en = options.version_en;
    } else {
      const name = 'version';
      const msg = 'バージョンの形式を選択してください';
      showErrorFlush(msg);
      return DeferredUtility.resolvedPromise({ result: false, msg, errors: { [name]: [['runtime', msg]] } });
    }
    if (postData.is_inst === '') {
      const name = 'is_inst';
      const msg = '歌詞の有無を選択してください';
      showErrorFlush(msg);
      return DeferredUtility.resolvedPromise({ result: false, msg, errors: { [name]: [['runtime', msg]] } });
    }
    options.music_featuring_artists.forEach((musicFeaturingArtist, index: number) => {
      postData[`music_featuring_artists[${index}][name]`] = musicFeaturingArtist.name;
      postData[`music_featuring_artists[${index}][name_kana]`] = musicFeaturingArtist.name_kana;
      postData[`music_featuring_artists[${index}][name_en]`] = musicFeaturingArtist.name_en;
    });
    options.music_lyric_writers.forEach((musicLyricWriter, index: number) => {
      postData[`music_lyric_writers[${index}][name]`] = musicLyricWriter.name;
      postData[`music_lyric_writers[${index}][name_en]`] = musicLyricWriter.name_en;
    });
    options.music_composers.forEach((musicComposer, index: number) => {
      postData[`music_composers[${index}][name]`] = musicComposer.name;
      postData[`music_composers[${index}][name_en]`] = musicComposer.name_en;
    });
    options.music_production_and_engineering_roles.forEach((musicProductionAndEngineeringRole, index: number) => {
      postData[`music_production_and_engineering_roles[${index}][role]`] = musicProductionAndEngineeringRole.role;
      postData[`music_production_and_engineering_roles[${index}][name]`] = musicProductionAndEngineeringRole.name;
      postData[`music_production_and_engineering_roles[${index}][name_en]`] = musicProductionAndEngineeringRole.name_en;
    });
    options.music_performance_roles.forEach((musicPerformanceRole, index: number) => {
      postData[`music_performance_roles[${index}][role]`] = musicPerformanceRole.role;
      postData[`music_performance_roles[${index}][name]`] = musicPerformanceRole.name;
      postData[`music_performance_roles[${index}][name_en]`] = musicPerformanceRole.name_en;
    });
    return ajaxPost(`/musics/ajax_update/${musicId}`, postData, {}, true);
  },
};

export const MusicDistributionClientContext = React.createContext(defaultMusicDistributionClient);
